const state = {
  range: null,
  usersList: null,
  teamsList: null,
  pipelinesList: null,
  mailboxesList: null,
  sequencesList: null,
  routingFormsList: null,
  meetingAgentsList: null,
  filters: null,

  selectedDashboard: null,
  loadingDashboard: false,
  showExitModal: false,
  chartInProgress: false,
  messagesCount: 0,

  selectedChartType: null,
  chartConfiguration: {},
  selectedObjects: {
    primary: null,
    secondary: [],
  },
  objectSchemas: {},
  availableProperties: [],
  sqlQuery: "",
  configurationExists: false,
};

const mutations = {
  SET_RANGE(state, range) {
    state.range = range;
  },
  SET_USERS_LIST(state, usersList) {
    state.usersList = usersList;
  },
  SET_TEAMS_LIST(state, teamList) {
    state.teamsList = teamList;
  },
  SET_PIPELINES_LIST(state, pipelinesList) {
    state.pipelinesList = pipelinesList;
  },
  SET_MAILBOXES_LIST(state, mailboxesList) {
    state.mailboxesList = mailboxesList;
  },
  SET_SEQUENCES_LIST(state, sequencesList) {
    state.sequencesList = sequencesList;
  },
  SET_ROUTING_FORMS_LIST(state, routingFormsList) {
    state.routingFormsList = routingFormsList;
  },
  SET_MEETING_AGENTS_LIST(state, meetingAgentsList) {
    state.meetingAgentsList = meetingAgentsList;
  },
  SET_FILTERS(state, filters) {
    state.filters = filters;
  },
  SET_SELECTED_DASHBOARD(state, dashboard) {
    state.selectedDashboard = dashboard;
  },
  SET_LOADING_DASHBOARD(state, val) {
    state.loadingDashboard = val;
  },
  SET_SHOW_EXIT_MODAL(state, val) {
    state.showExitModal = val;
  },
  SET_CHART_IN_PROGRESS(state, chartInProgress) {
    state.chartInProgress = chartInProgress;
  },
  SET_MESSAGES_COUNT(state, count) {
    state.messagesCount = count;
  },
  SET_SELECTED_CHART_TYPE(state, chartType) {
    state.selectedChartType = chartType;
  },
  SET_CHART_CONFIGURATION(state, config) {
    state.chartConfiguration = config;
  },
  UPDATE_CHART_CONFIGURATION_FIELD(state, { field, value }) {
    state.chartConfiguration = {
      ...state.chartConfiguration,
      [field]: value,
    };
  },
  SET_SELECTED_OBJECTS(state, { primary, secondary }) {
    state.selectedObjects = {
      primary: primary || null,
      secondary: secondary || [],
    };
  },
  SET_OBJECT_SCHEMA(state, { slug, schema }) {
    state.objectSchemas = {
      ...state.objectSchemas,
      [slug]: schema,
    };
  },
  SET_AVAILABLE_PROPERTIES(state, properties) {
    state.availableProperties = properties;
  },
  SET_SQL_QUERY(state, query) {
    state.sqlQuery = query;
  },
  SET_CONFIGURATION_EXISTS(state, exists) {
    state.configurationExists = exists;
  },
  RESET_CONFIGURATION(state) {
    state.selectedChartType = null;
    state.chartConfiguration = {};
    state.selectedObjects = {
      primary: null,
      secondary: [],
    };
    state.objectSchemas = {};
    state.availableProperties = [];
    state.sqlQuery = "";
    state.configurationExists = false;
  },
};

const actions = {
  updateRange({ commit }, range) {
    commit("SET_RANGE", range);
  },
  updateUsersList({ commit }, usersList) {
    commit("SET_USERS_LIST", usersList);
  },
  updateTeamsList({ commit }, teamsList) {
    commit("SET_TEAMS_LIST", teamsList);
  },
  updatePipelinesList({ commit }, pipelinesList) {
    commit("SET_PIPELINES_LIST", pipelinesList);
  },
  updateMailboxesList({ commit }, mailboxesList) {
    commit("SET_MAILBOXES_LIST", mailboxesList);
  },
  updateSequencesList({ commit }, sequencesList) {
    commit("SET_SEQUENCES_LIST", sequencesList);
  },
  updateRoutingFormsList({ commit }, routingFormsList) {
    commit("SET_ROUTING_FORMS_LIST", routingFormsList);
  },
  updateMeetingAgentsList({ commit }, meetingAgentsList) {
    commit("SET_MEETING_AGENTS_LIST", meetingAgentsList);
  },
  updateFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },
  setSelectedDashboard({ commit }, dashboard) {
    commit("SET_SELECTED_DASHBOARD", dashboard);
  },
  setChartInProgress({ commit }, chartInProgress) {
    commit("SET_CHART_IN_PROGRESS", chartInProgress);
  },
  setMessagesCount({ commit }, count) {
    commit("SET_MESSAGES_COUNT", count);
  },
  setSelectedChartType({ dispatch }, chartType) {
    dispatch("analyticsConfiguration/setSelectedChartType", chartType, {
      root: true,
    });
  },
  setChartConfiguration({ dispatch }, config) {
    dispatch("analyticsConfiguration/setChartConfiguration", config, {
      root: true,
    });
  },
  updateChartConfigurationField({ dispatch }, { field, value }) {
    dispatch(
      "analyticsConfiguration/updateChartConfigurationField",
      { field, value },
      { root: true },
    );
  },
  async updateSelectedObjects({ dispatch }, { primary, secondary }) {
    await dispatch(
      "analyticsConfiguration/updateSelectedObjects",
      { primary, secondary },
      { root: true },
    );
  },
  async fetchObjectSchema({ commit, state }, object) {
    try {
      if (!object || !object.slug) return;

      if (state.objectSchemas[object.slug]) {
        return;
      }

      const CRMService = (await import("@/src/api/CRMService.js")).default;
      const response = await CRMService.fetchSchemaObject(object.slug);

      if (response?.success && response?.object) {
        commit("SET_OBJECT_SCHEMA", {
          slug: object.slug,
          schema: response.object,
        });
      }
    } catch (error) {
      console.error(`Error fetching schema for object ${object.slug}:`, error);
    }
  },
  buildAvailableProperties({ state, commit }) {
    const { selectedObjects, objectSchemas } = state;
    const properties = [];

    const allObjects = [
      selectedObjects.primary,
      ...selectedObjects.secondary,
    ].filter(Boolean);

    const multipleObjects = allObjects.length > 1;

    allObjects.forEach((object) => {
      const schema = objectSchemas[object.slug];
      if (schema && schema.attributes) {
        schema.attributes.forEach((attr) => {
          const propertyName = multipleObjects
            ? `${schema.name}.${attr.name}`
            : attr.name;

          properties.push({
            id: attr.id,
            name: propertyName,
            type: attr.type,
            dataType: attr.data_type || attr.type,
            objectId: object.id,
            objectSlug: object.slug,
            objectName: schema.name,
            originalName: attr.name,
          });
        });
      }
    });

    commit("SET_AVAILABLE_PROPERTIES", properties);
  },
  setSqlQuery({ dispatch }, query) {
    dispatch("analyticsConfiguration/setSqlQuery", query, { root: true });
  },
  setConfigurationExists({ dispatch }, exists) {
    dispatch("analyticsConfiguration/setConfigurationExists", exists, {
      root: true,
    });
  },
  resetConfiguration({ dispatch }) {
    dispatch("analyticsConfiguration/resetConfiguration", null, { root: true });
  },
  setLoadingDashboard({ commit }, val) {
    commit("SET_LOADING_DASHBOARD", val);
  },
  setShowExitModal({ commit }, val) {
    commit("SET_SHOW_EXIT_MODAL", val);
  },
  resetState({ commit, dispatch }) {
    commit("SET_SELECTED_DASHBOARD", null);
    commit("SET_LOADING_DASHBOARD", false);
    commit("SET_SHOW_EXIT_MODAL", false);
    commit("SET_CHART_IN_PROGRESS", false);
    commit("RESET_CONFIGURATION");
    dispatch("analyticsConfiguration/resetConfiguration", null, { root: true });
  },
};

const getters = {
  getRange: (state) => state.range,
  getUsersList: (state) => state.usersList,
  getTeamsList: (state) => state.teamsList,
  getPipelinesList: (state) => state.pipelinesList,
  getMailboxesList: (state) => state.mailboxesList,
  getSequencesList: (state) => state.sequencesList,
  getRoutingFormsList: (state) => state.routingFormsList,
  getMeetingAgentsList: (state) => state.meetingAgentsList,
  getFilters: (state) => state.filters,

  getUserIds: (state) => {
    if (!state.filters || !state.filters.user) return [];
    return Array.isArray(state.filters.user)
      ? state.filters.user.map((user) => user.id)
      : [state.filters.user.id];
  },

  getTeamIds: (state) => {
    if (!state.filters || !state.filters.team) return [];
    return Array.isArray(state.filters.team)
      ? state.filters.team.map((team) => team.id)
      : [state.filters.team.id];
  },

  getSequenceIds: (state) => {
    if (!state.filters || !state.filters.sequence) return [];
    return Array.isArray(state.filters.sequence)
      ? state.filters.sequence.map((sequence) => sequence.id)
      : [state.filters.sequence.id];
  },

  selectedDashboard: (state) => state.selectedDashboard,
  loadingDashboard: (state) => state.loadingDashboard,
  showExitModal: (state) => state.showExitModal,
  selectedDashboardID: (state) => state.selectedDashboard?.id,
  dashboardType: (state) => state.selectedDashboard?.type,
  chartInProgress: (state) => state.chartInProgress,
  messagesCount: (state) => state.messagesCount,
  selectedChartType: (state) => state.selectedChartType,
  chartConfiguration: (state) => state.chartConfiguration,
  selectedObjects: (state) => state.selectedObjects,
  objectSchemas: (state) => state.objectSchemas,
  availableProperties: (state) => state.availableProperties,
  sqlQuery: (state) => state.sqlQuery,
  configurationExists: (state) => state.configurationExists,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
